/**
 * Registration display helpers shared between the admin signups view and the export action.
 */

import type { ActivityRow, RegistrationStatus } from "../types/db-types";
import type { EmailTemplate, TemplateKind } from "../../_components/settings/notifications/templates";

/** Display label + Tailwind tag classes for each registration status. */
export const REGISTRATION_STATUS_META: Record<RegistrationStatus, { label: string; tagClass: string }> = {
  registered: { label: "Registered", tagClass: "bg-indigo-100 text-indigo-700" },
  waitlisted: { label: "Waitlisted", tagClass: "bg-amber-100 text-amber-800" },
  cancelled: { label: "Cancelled", tagClass: "bg-red-100 text-red-800" },
};

/** Order registrations are presented in across filters and grouped pickers. */
export const REGISTRATION_STATUS_ORDER: RegistrationStatus[] = ["registered", "waitlisted", "cancelled"];

/**
 * Splits a stored full-name string into first and last parts using the first
 * whitespace as the boundary. Mirrors how the signup form joins names
 * (`${firstName} ${lastName}`) when persisting to `activity_registrations.name`.
 *
 * @param name - The stored full-name string.
 * @returns First name (everything before the first space) and last name (the rest).
 */
export function splitFullName(name: string | null | undefined): { firstName: string; lastName: string } {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return { firstName: "", lastName: "" };
  const spaceIndex = trimmed.indexOf(" ");
  if (spaceIndex === -1) return { firstName: trimmed, lastName: "" };
  return {
    firstName: trimmed.slice(0, spaceIndex).trim(),
    lastName: trimmed.slice(spaceIndex + 1).trim(),
  };
}

/** Email kinds that an admin-driven status transition can fire from `updateRegistration`. */
export type StatusChangeEmailKind = "promoted" | "cancelled" | "confirmation";

/**
 * Maps a registration status transition to the email kind it would fire, or null when
 * no email is associated. Shared between the EditRegistrationModal (to decide whether to
 * show the "send email" toggle and what to label it) and the server action (to gate the
 * appropriate `sendActivityEmail` call).
 *
 * Current mappings:
 * - `waitlisted` → `registered`: `"promoted"`
 * - `cancelled` → `registered`: `"confirmation"` (re-registration after a cancel)
 * - any → `cancelled` (except `cancelled` → `cancelled`): `"cancelled"`
 * - everything else: `null`
 *
 * @param fromStatus - The registration's current status.
 * @param toStatus - The status the registration is being changed to.
 * @returns The email kind that would fire, or `null` if no email is associated with the transition.
 */
export function getEmailKindForStatusChange(
  fromStatus: RegistrationStatus,
  toStatus: RegistrationStatus
): StatusChangeEmailKind | null {
  if (fromStatus === toStatus) return null;
  if (toStatus === "cancelled") return "cancelled";
  if (fromStatus === "waitlisted" && toStatus === "registered") return "promoted";
  if (fromStatus === "cancelled" && toStatus === "registered") return "confirmation";
  return null;
}

/**
 * Default for the "send email on status change" toggle: the activity-level override falls
 * back to the event-level template default. Shared so the inline status-change confirm
 * (SignupsView) and the EditRegistrationModal don't drift.
 */
export function getDefaultNotifyForStatusChange(
  kind: StatusChangeEmailKind,
  activity:
    | Pick<ActivityRow, "auto_email_registered" | "auto_email_promoted" | "auto_email_cancelled">
    | null
    | undefined,
  templates: Pick<Record<TemplateKind, EmailTemplate>, "confirmation" | "promoted" | "cancelled">
): boolean {
  if (kind === "promoted") return activity?.auto_email_promoted ?? templates.promoted.isAutomatic;
  if (kind === "confirmation") return activity?.auto_email_registered ?? templates.confirmation.isAutomatic;
  return activity?.auto_email_cancelled ?? templates.cancelled.isAutomatic;
}
